import isDefined from "../../Core/isDefined";

/** `Dimension` (and child interfaces - eg `EnumDimension`, `NumericalDimension`, ...) are Trait/JSON friendly interfaces. They are used as base to the `SelectableDimension` interfaces.
 *
 * This is useful because it means we can directly use Traits to create SelectableDimensions - for example see `EnumDimensionTraits` in `lib/Traits/TraitsClasses/DimensionTraits.ts`
 */

/** Similar to EnumDimension, but supports multiple selected values */

export const DEFAULT_PLACEMENT = "default";

/** Base SelectableDimension interface. Each following SelectableDimension will extend this and the Dimension interface above */

/** Similar to SelectableDimensionEnum, but supports multiple selected values */

export const isEnum = dim => dim.type === "select" || dim.type === undefined;
export const isMultiEnum = dim => dim.type === "select-multi";

/** Return only SelectableDimensionSelect from array of SelectableDimension */
export const filterEnums = dims => dims.filter(isEnum);
export const isGroup = dim => dim.type === "group";
export const isNumeric = dim => dim.type === "numeric";
export const isText = dim => dim.type === "text";
export const isButton = dim => dim.type === "button";
export const isCheckbox = dim => dim.type === "checkbox";
export const isCheckboxGroup = dim => dim.type === "checkbox-group";
export const isColor = dim => dim.type === "color";
const isCorrectPlacement = placement => dim => dim.placement ? dim.placement === placement : placement === DEFAULT_PLACEMENT;
const isEnabled = dim => !dim.disable;

/** Filter out dimensions with only 1 option (unless they have 1 option and allow undefined - which is 2 total options) */
const enumHasValidOptions = dim => {
  const minLength = dim.allowUndefined ? 1 : 2;
  return isDefined(dim.options) && dim.options.length >= minLength;
};

/** Multi enums just need one option (they don't have `allowUndefined`) */
const multiEnumHasValidOptions = dim => {
  return isDefined(dim.options) && dim.options.length > 0;
};

/** Filter with SelectableDimension should be shown for a given placement.
 * This will take into account whether SelectableDimension is valid, not disabled, etc...
 */
export const filterSelectableDimensions = placement => function () {
  let selectableDimensions = arguments.length > 0 && arguments[0] !== undefined ? arguments[0] : [];
  return selectableDimensions.filter(dim =>
  // Filter by placement if defined, otherwise use default placement
  (!isDefined(placement) || isCorrectPlacement(placement)(dim)) && isEnabled(dim) && (
  // Check enum (select and checkbox) dimensions for valid options
  !isEnum(dim) && !isCheckbox(dim) || enumHasValidOptions(dim)) && (
  // Check multi-enum
  !isMultiEnum(dim) || multiEnumHasValidOptions(dim)) && (
  // Only show groups if they have at least one SelectableDimension
  !isGroup(dim) || dim.selectableDimensions.length > 0));
};

/** Find human readable name for the current value for a SelectableDimension */
export const findSelectedValueName = dim => {
  if (isCheckbox(dim)) {
    return dim.selectedId === "true" ? "Enabled" : "Disabled";
  }
  if (isEnum(dim)) {
    var _dim$options;
    return (_dim$options = dim.options) === null || _dim$options === void 0 || (_dim$options = _dim$options.find(opt => opt.id === dim.selectedId)) === null || _dim$options === void 0 ? void 0 : _dim$options.name;
  }
  if (isMultiEnum(dim)) {
    var _dim$options2;
    // return names as CSV
    return (_dim$options2 = dim.options) === null || _dim$options2 === void 0 || (_dim$options2 = _dim$options2.filter(opt => {
      var _dim$selectedIds;
      return (_dim$selectedIds = dim.selectedIds) === null || _dim$selectedIds === void 0 ? void 0 : _dim$selectedIds.some(id => opt.id === id);
    })) === null || _dim$options2 === void 0 || (_dim$options2 = _dim$options2.map(option => option.name)) === null || _dim$options2 === void 0 ? void 0 : _dim$options2.join(", ");
  }
  if (isNumeric(dim)) {
    var _dim$value;
    return (_dim$value = dim.value) === null || _dim$value === void 0 ? void 0 : _dim$value.toString();
  }
  if (isText(dim)) return dim.value;
};

/** Interface to be implemented by BaseModels (eg CatalogMembers) to add selectableDimensions */
let SelectableDimensions;
(function (_SelectableDimensions) {
  function is(model) {
    return "selectableDimensions" in model;
  }
  _SelectableDimensions.is = is;
})(SelectableDimensions || (SelectableDimensions = {}));
export default SelectableDimensions;